import { arrayMove } from '@dnd-kit/sortable';
import { ColumnDef } from '@tanstack/react-table';
import { DetailedHTMLProps, memo, TableHTMLAttributes, useMemo } from 'react';

import { merge } from '@/components';
import { Button, Flex, Table, Text, useComponentStack } from '@/components/core';
import { AddIcon } from '@/components/icons';
import { AddFieldButton, FieldCell, NavigationContainer } from '@/components/ui';
import { FieldState, useModelConfigStore } from '@/store';

import { FieldDetails } from './FieldDetails';

export type FieldConfigProps = DetailedHTMLProps<TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>;

const IFieldConfig = ({ className, ...rest }: FieldConfigProps) => {
  const fields = useModelConfigStore((state) => state.fields);
  const addField = useModelConfigStore((state) => state.addField);
  const setFields = useModelConfigStore((state) => state.setFields);
  const duplicateField = useModelConfigStore((state) => state.duplicateField);
  const deleteField = useModelConfigStore((state) => state.deleteField);

  const componentStack = useComponentStack();

  const columns = useMemo<ColumnDef<FieldState>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: ({ row }) => (
          <FieldCell
            field={row.original}
            onFieldItemClick={(item) =>
              componentStack?.push(
                <NavigationContainer>
                  <FieldDetails fieldId={item.id} />
                </NavigationContainer>
              )
            }
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ],
    [componentStack]
  );

  return (
    <>
      <Table
        className={merge('border-separate border-spacing-x-0 border-spacing-y-2 p-4', className)}
        data={fields}
        columns={columns}
        isInnerTable={false}
        onRowIndexChange={(oldIndex, newIndex) => setFields(arrayMove(fields, oldIndex, newIndex))}
        hideHeader
        duplicateRowAction={(row) => duplicateField(row.original.id)}
        deleteRowAction={(row) => deleteField(row.original.id)}
        {...rest}
      />
      <Flex className="sticky bottom-0 z-10 gap-2 p-2">
        <AddFieldButton variant="secondary">
          <AddIcon />
          <Text>Add field</Text>
        </AddFieldButton>
        <Button onPress={() => addField({ type: 'table', name: 'New table' })} variant="plain">
          <AddIcon />
          <Text>Add table</Text>
        </Button>
      </Flex>
    </>
  );
};

export const FieldConfig = memo(IFieldConfig) as typeof IFieldConfig;
